import { useEffect, useMemo, useState } from 'react';
import { useQuestionnaire } from 'src/use-cases/invoice/useQuestionnaire';
import { useGetVersion } from 'src/use-cases/invoice/useInvoices.tsx';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { DeviationDto, DeviationQuestionUserResponseDto, DoubleChargeLineDecisionDto, InvoiceSummaryDto } from 'src/generated/services/TFinancialApi';
import { useGetUserResponse, useUpdateUserResponseDeviation } from 'src/use-cases/invoice/useUserResponse';
import { useShipmentSelector } from '../../../ShipmentSelector';

/**
 *
 * @param invoiceDetail Can be null, due to Courier Invoice don't have invoice detail
 * @param selectedDeviationId
 * @returns
 */
// TODO: move Approval to another hook
export const useDeviationForm = (invoiceSummary: InvoiceSummaryDto | null, selectedDeviation: DeviationDto) => {
  const { mutateAsync: updateResponse } = useUpdateUserResponseDeviation();
  const { data: allQuestionData } = useQuestionnaire(selectedDeviation.id);
  const { data: allResponseData } = useGetUserResponse(selectedDeviation.id);

  const { shipment } = useShipmentSelector();

  // Get current deviation to check isStatusAssignedToCurrentUser
  const currentDeviation = shipment?.deviations?.find((deviation) => deviation.id === selectedDeviation?.id);

  const [isProcurementReadOnly, setIsProcurementReadOnly] = useState(false);
  const [isLogisticReadOnly, setIsLogisticReadOnly] = useState(false);
  const [isSupplyChainReadOnly, setIsSupplyChainReadOnly] = useState(false);
  const [isFinanceReadOnly, setIsFinanceReadOnly] = useState(false);
  const [isOpsAdminReadOnly, setIsOpsAdminReadOnly] = useState(false);

  const isLogistic = invoiceSummary?.currentMainRole === 'OPERATION';
  const isProcurement = invoiceSummary?.currentMainRole === 'PROCUREMENT';
  const isSupplyChain = invoiceSummary?.currentMainRole === 'SUPPLY_CHAIN';
  const isFinance = invoiceSummary?.currentMainRole === 'FINANCE';
  const isOpsAdmin = invoiceSummary?.currentMainRole === 'OPS_ADMIN';

  const logisticQuestions = allQuestionData?.data?.OPERATION_REVIEW;
  const procurementQuestions = allQuestionData?.data?.PROCUREMENT_REVIEW;
  const supplyChainQuestions = allQuestionData?.data?.SUPPLY_CHAIN_REVIEW;
  const financeQuestions = allQuestionData?.data?.FINANCE_REVIEW;
  const opsAdminQuestions = allQuestionData?.data?.OPS_ADMIN_REVIEW;

  const logisticResponse = allResponseData?.data?.OPERATION_REVIEW;
  const procurementResponse = allResponseData?.data?.PROCUREMENT_REVIEW;
  const supplyChainResponse = allResponseData?.data?.SUPPLY_CHAIN_REVIEW;
  const financeResponse = allResponseData?.data?.FINANCE_REVIEW;
  const opsAdminResponse = allResponseData?.data?.OPS_ADMIN_REVIEW;

  // Update readOnly state for logistic
  useEffect(() => {
    let isReadOnly = false;
    if (
      invoiceSummary?.invoiceDispute &&
      selectedDeviation &&
      invoiceSummary?.invoiceDispute.status === 'CANCELED' &&
      selectedDeviation.status !== 'UNFILLED'
    ) {
      isReadOnly = true;
    } else if (
      (!invoiceSummary?.invoiceDispute || invoiceSummary?.invoiceDispute?.status !== 'CANCELED') &&
      logisticResponse?.length > 0 &&
      logisticQuestions?.length > 0
    ) {
      isReadOnly = true;
    }
    setIsLogisticReadOnly(isReadOnly);
  }, [logisticResponse, logisticQuestions, invoiceSummary, selectedDeviation]);

  // Update readOnly state for procurement
  useEffect(() => {
    let isReadOnly = false;
    if (
      invoiceSummary?.invoiceDispute &&
      selectedDeviation &&
      invoiceSummary?.invoiceDispute.status === 'CANCELED' &&
      selectedDeviation.status !== 'UNFILLED'
    ) {
      isReadOnly = true;
    } else if (
      (!invoiceSummary?.invoiceDispute || invoiceSummary?.invoiceDispute?.status !== 'CANCELED') &&
      procurementResponse?.length > 0 &&
      procurementQuestions?.length > 0
    ) {
      isReadOnly = true;
    }
    setIsProcurementReadOnly(isReadOnly);
  }, [procurementResponse, procurementQuestions, invoiceSummary, selectedDeviation]);

  // Update readOnly state for supply chain
  useEffect(() => {
    let isReadOnly = false;
    if (
      invoiceSummary?.invoiceDispute &&
      selectedDeviation &&
      invoiceSummary?.invoiceDispute.status === 'CANCELED' &&
      selectedDeviation.status !== 'UNFILLED'
    ) {
      isReadOnly = true;
    } else if (
      (!invoiceSummary?.invoiceDispute || invoiceSummary?.invoiceDispute?.status !== 'CANCELED') &&
      supplyChainResponse?.length > 0 &&
      supplyChainQuestions?.length > 0
    ) {
      isReadOnly = true;
    }
    setIsSupplyChainReadOnly(isReadOnly);
  }, [supplyChainResponse, supplyChainQuestions, invoiceSummary, selectedDeviation]);

  // Update readOnly state for finance
  useEffect(() => {
    let isReadOnly = false;
    if (
      invoiceSummary?.invoiceDispute &&
      selectedDeviation &&
      invoiceSummary?.invoiceDispute.status === 'CANCELED' &&
      selectedDeviation.status !== 'UNFILLED'
    ) {
      isReadOnly = true;
    } else if (
      (!invoiceSummary?.invoiceDispute || invoiceSummary?.invoiceDispute?.status !== 'CANCELED') &&
      financeResponse?.length > 0 &&
      financeQuestions?.length > 0
    ) {
      isReadOnly = true;
    }
    setIsFinanceReadOnly(isReadOnly);
  }, [financeResponse, financeQuestions, invoiceSummary, selectedDeviation]);

  // Update readOnly state for ops admin
  useEffect(() => {
    let isReadOnly = false;
    if (
      invoiceSummary?.invoiceDispute &&
      selectedDeviation &&
      invoiceSummary?.invoiceDispute.status === 'CANCELED' &&
      selectedDeviation.status !== 'UNFILLED'
    ) {
      isReadOnly = true;
    } else if (
      (!invoiceSummary?.invoiceDispute || invoiceSummary?.invoiceDispute?.status !== 'CANCELED') &&
      opsAdminResponse?.length > 0 &&
      opsAdminQuestions?.length > 0
    ) {
      isReadOnly = true;
    }
    setIsOpsAdminReadOnly(isReadOnly);
  }, [opsAdminResponse, opsAdminQuestions, invoiceSummary, selectedDeviation]);

  const { invoiceId, version, setIsShowErrorVersion } = useInvoiceContext();

  const { refetch: refetchVersion } = useGetVersion(invoiceId);

  const { canGiveOpsReview, canGiveProcurementReview, canGiveSupplyChainReview, canGiveFinanceReview, canGiveOpsAdminReview } = useMemo(() => {
    const permissions = shipment?.permissions;
    if (!permissions) return {};
    return {
      canGiveOpsReview: permissions.includes('CAN_GIVE_OPERATION_REVIEW'),
      canGiveProcurementReview: permissions.includes('CAN_GIVE_PROCUREMENT_REVIEW'),
      canGiveSupplyChainReview: permissions.includes('CAN_GIVE_SUPPLY_CHAIN_REVIEW'),
      canGiveFinanceReview: permissions.includes('CAN_GIVE_FINANCE_REVIEW'),
      canGiveOpsAdminReview: permissions.includes('CAN_GIVE_OPS_ADMIN_REVIEW'),
    };
  }, [shipment]);

  const isLoading = !allQuestionData || !allResponseData || !invoiceSummary;

  // Define workflow path guards to prevent overlap between workflows
  const isStandardWorkflow = useMemo(
    () => ['OPERATION_REVIEW', 'PROCUREMENT_REVIEW'].includes(shipment.currentDeviationWorkflow || ''),
    [shipment.currentDeviationWorkflow]
  );

  const isThresholdWorkflow = useMemo(
    () => ['SUPPLY_CHAIN_REVIEW', 'FINANCE_REVIEW'].includes(shipment.currentDeviationWorkflow || ''),
    [shipment.currentDeviationWorkflow]
  );

  const isOpsAdminWorkflow = useMemo(() => shipment.currentDeviationWorkflow === 'OPS_ADMIN_REVIEW', [shipment.currentDeviationWorkflow]);

  const isProcurementEditable = useMemo(
    () =>
      isProcurement &&
      canGiveProcurementReview &&
      shipment.currentDeviationWorkflow === 'PROCUREMENT_REVIEW' &&
      currentDeviation?.isStatusAssignedToCurrentUser === true &&
      invoiceSummary?.status !== 'APPROVED',
    [shipment, isProcurement, canGiveProcurementReview, currentDeviation, invoiceSummary]
  );
  const isLogisticEditable = useMemo(
    () =>
      isLogistic &&
      canGiveOpsReview &&
      shipment.currentDeviationWorkflow === 'OPERATION_REVIEW' &&
      currentDeviation?.isStatusAssignedToCurrentUser === true &&
      invoiceSummary?.status !== 'APPROVED',
    [shipment, isLogistic, canGiveOpsReview, currentDeviation, invoiceSummary]
  );
  const isSupplyChainEditable = useMemo(
    () =>
      isSupplyChain &&
      canGiveSupplyChainReview &&
      shipment.currentDeviationWorkflow === 'SUPPLY_CHAIN_REVIEW' &&
      currentDeviation?.isStatusAssignedToCurrentUser === true &&
      invoiceSummary?.status !== 'APPROVED',
    [shipment, isSupplyChain, canGiveSupplyChainReview, currentDeviation, invoiceSummary]
  );
  const isFinanceEditable = useMemo(
    () =>
      isFinance &&
      canGiveFinanceReview &&
      shipment.currentDeviationWorkflow === 'FINANCE_REVIEW' &&
      currentDeviation?.isStatusAssignedToCurrentUser === true &&
      invoiceSummary?.status !== 'APPROVED',
    [shipment, isFinance, canGiveFinanceReview, currentDeviation, invoiceSummary]
  );
  const isOpsAdminEditable = useMemo(
    () =>
      isOpsAdmin &&
      canGiveOpsAdminReview &&
      shipment.currentDeviationWorkflow === 'OPS_ADMIN_REVIEW' &&
      currentDeviation?.isStatusAssignedToCurrentUser === true &&
      invoiceSummary?.status !== 'APPROVED',
    [shipment, isOpsAdmin, canGiveOpsAdminReview, currentDeviation, invoiceSummary]
  );

  const handleSubmit = async (data: DeviationQuestionUserResponseDto[], newChargeAnswer?: any, doubleChargeLineDecisions?: DoubleChargeLineDecisionDto[]) => {
    const respVersion = await refetchVersion();

    if (version && respVersion?.data && version !== respVersion?.data) {
      setIsShowErrorVersion(true);
    } else {
      await updateResponse({
        deviationId: selectedDeviation.id,
        data: {
          questionnaireAnswer: data,
          newChargeAnswer,
          doubleChargeLineDecisions,
        },
      });
    }
  };

  return {
    updateResponse,
    allQuestionData,
    allResponseData,
    logisticQuestions,
    procurementQuestions,
    supplyChainQuestions,
    financeQuestions,
    opsAdminQuestions,
    logisticResponse,
    procurementResponse,
    supplyChainResponse,
    financeResponse,
    opsAdminResponse,
    invoiceSummary,
    invoiceId,
    isLogistic,
    isProcurement,
    isSupplyChain,
    isFinance,
    isOpsAdmin,
    isProcurementEditable,
    isLogisticEditable,
    isSupplyChainEditable,
    isFinanceEditable,
    isOpsAdminEditable,
    canGiveOpsReview,
    canGiveProcurementReview,
    canGiveSupplyChainReview,
    canGiveFinanceReview,
    canGiveOpsAdminReview,
    isProcurementReadOnly,
    setIsProcurementReadOnly,
    isLogisticReadOnly,
    setIsLogisticReadOnly,
    isSupplyChainReadOnly,
    setIsSupplyChainReadOnly,
    isFinanceReadOnly,
    setIsFinanceReadOnly,
    isOpsAdminReadOnly,
    setIsOpsAdminReadOnly,
    isLoading,
    handleSubmit,
    // Workflow path guards
    isStandardWorkflow,
    isThresholdWorkflow,
    isOpsAdminWorkflow,
  } as const;
};
